import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { ReplaySubject } from 'rxjs';
import { MarineInsuranceQuoteFormModel } from '@shared/marine-insurance-shared/models';
import { MarineInsuranceManagementModel } from '@data-layer/models/marine-insurance';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumMarineInsuranceCommodity } from '@app/data-layer/enums/marine-insurance';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { DateTimezoneService } from '@services/date-timezone.service';
import { DateHelper } from '@helpers/date.helper';

@Component({
  selector: 'app-marine-insurance-sidebar-description-management-form',
  templateUrl: './marine-insurance-sidebar-description-management-form.component.html',
  styleUrls: ['./marine-insurance-sidebar-description-management-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceSidebarDescriptionManagementFormComponent implements OnInit, OnDestroy {
  @Input() public marineInsurance: MarineInsuranceManagementModel;
  @Input() public isSaving: boolean;

  @Output() public save: EventEmitter<MarineInsuranceQuoteFormModel> = new EventEmitter<MarineInsuranceQuoteFormModel>();

  public formGroup: UntypedFormGroup;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public errors: { [name: string]: IErrorForm } = {
    manufactureDate: {
      required: 'FORM_ERROR.REQUIRED',
    },
  };
  public maxManufaturerDate: string = this.dateTimeZoneService.endOfTheDay(DateHelper.now);

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private dateTimeZoneService: DateTimezoneService) {}

  public ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      manufactureDate: new UntypedFormControl(null, this.isVehiclesCommodity ? Validators.required : []),
      goodsTypeDescription: new UntypedFormControl(null),
    });

    if (!!this.marineInsurance) {
      this.formGroup.patchValue({
        manufactureDate: this.manufactureDate,
        goodsTypeDescription: !!this.marineInsurance?.updatedInsuredValue?.goodsTypeDescription
          ? this.marineInsurance?.updatedInsuredValue?.goodsTypeDescription
          : this.marineInsurance?.insuredValue?.goodsTypeDescription,
      });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSave(): void {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.valid) {
      this.isSaving = true;
      const manufactureDateControl = this.formGroup.get('manufactureDate');
      const manufactureDateYear = manufactureDateControl.value.toString();
      const manufactureDate = new Date(manufactureDateYear).getFullYear().toString();

      this.save.emit({
        ...this.formGroup.getRawValue(),
        manufactureDate,
      });
    }
  }

  public get isVehiclesCommodity(): boolean {
    return this.marineInsurance?.insuredValue?.commodity?.id === enumMarineInsuranceCommodity.VEHICLES;
  }

  public get manufactureDate(): string {
    //Conversion of year string into full date string with year assuming we convert date to year only string upon saving
    const originalManufactureDate = this.marineInsurance?.updatedManufactureDate || this.marineInsurance?.manufactureDate;

    if (!originalManufactureDate) {
      return;
    }

    const newManufactureDate = this.dateTimeZoneService.formatInUserTZ(`${originalManufactureDate}-02-02`);
    return newManufactureDate;
  }
}
